import { avatarColorFor } from '@/shared/lib/avatarColor';
import { Icon } from '@/shared/ui/Icon';

interface TransferCardProps {
  fromName: string;
  toName: string;
  amount: number;
  completed?: boolean;
  onToggle?: () => void;
}

const currency = new Intl.NumberFormat('ko-KR');

function Avatar({ name }: { name: string }) {
  return (
    <span
      className="inline-flex h-8 w-8 items-center justify-center rounded-[10px] text-small font-semibold text-white"
      style={{ backgroundColor: avatarColorFor(name) }}
      aria-hidden="true"
    >
      {name.charAt(0)}
    </span>
  );
}

export function TransferCard({ fromName, toName, amount, completed = false, onToggle }: TransferCardProps) {
  const cardLabel = `${fromName}이(가) ${toName}에게 ${currency.format(amount)}원 ${completed ? '송금 완료' : '송금 예정'}`;

  return (
    <div
      role="group"
      aria-label={cardLabel}
      className="rounded-[18px] border border-surface-border/70 bg-surface/60 backdrop-blur-sm px-3 py-3"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5 text-ui font-semibold text-ink">
          <Avatar name={fromName} />
          <div className="min-w-0">
            <div className={`truncate text-[13px] font-semibold ${completed ? 'text-ink-muted line-through' : 'text-ink'}`}>
              {fromName}
            </div>
            <div className="mt-0.5 flex items-center gap-1 text-[10px] font-semibold text-primary">
              <Icon name="right" size={12} />
              <span>{toName}</span>
            </div>
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className={`num-display text-[18px] leading-none ${completed ? 'text-ink-muted line-through' : 'text-ink'}`}>
            {currency.format(amount)}
            <span className="ml-0.5 text-[12px] text-ink-faint">원</span>
          </div>
          <span
            className={`mt-1 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-semibold ${
              completed ? 'bg-success-soft text-success-strong' : 'bg-error-soft text-error'
            }`}
          >
            <span className="h-1.5 w-1.5 rounded-full bg-current" aria-hidden="true" />
            {completed ? '완료' : '미완료'}
          </span>
        </div>
      </div>

      {onToggle && (
        <button
          type="button"
          onClick={onToggle}
          aria-pressed={completed}
          className="mt-3 min-h-11 w-full rounded-xl border border-surface-border px-4 py-2 text-small font-semibold text-ink pressable hover:bg-surface-muted focus-visible:outline-none focus-visible:shadow-focus-ring"
        >
          {completed ? '완료 취소' : '송금 완료로 표시'}
        </button>
      )}
    </div>
  );
}
